import type { z } from 'zod';
import { ErrorWithHttpStatus } from '../error-with-http-status';

/**
 * The status a drifted response body is reported with.
 *
 * It has to be *a* status: every hook that fetches one of the validated endpoints types its error
 * as `ErrorWithHttpStatus`, and the views branch on `error.status`. Without one, `ErrorPage` falls
 * through its `switch` to the 404 page and tells a customer that the portfolio they own does not
 * exist.
 *
 * The value has to be this one. 401 and 410 are load-bearing elsewhere — the forced-relogin path in
 * `useGetAccessDetails` and the CloudFront stopgap in `fetchOrThrow` branch on them — and a
 * contract drift is neither an auth nor a gateway problem. 502 is what is left and what it is: the
 * request reached the server, and what came back is unusable.
 */
export const RESPONSE_VALIDATION_ERROR_STATUS = 502;

/**
 * A response that passed HTTP but does not carry the shape the UI compiles against.
 */
export class ApiResponseValidationError extends ErrorWithHttpStatus {
  public endpoint: string;
  public issueSummary: string;

  constructor(endpoint: string, issueSummary: string) {
    super(`Unexpected response shape from ${endpoint} — ${issueSummary}`, RESPONSE_VALIDATION_ERROR_STATUS);
    this.name = 'ApiResponseValidationError';
    this.endpoint = endpoint;
    this.issueSummary = issueSummary;
  }
}

/**
 * How many field-level problems get named before the summary is truncated. A drifted payload
 * usually breaks the same way in every element of an array, so the first few name the problem
 * and the rest only make the message unreadable.
 */
const MAX_REPORTED_ISSUES = 3;

/**
 * Check a response body against the schema declared for its endpoint, and describe the
 * mismatch if there is one.
 *
 * Returns the error rather than throwing it so that the caller decides how loud to be, and so
 * that the message is straightforward to assert on in tests.
 */
export function getResponseValidationError(params: {
  endpoint: string;
  schema: z.ZodTypeAny;
  body: unknown;
}): ApiResponseValidationError | undefined {
  const result = params.schema.safeParse(params.body);
  if (result.success) {
    return undefined;
  }
  return new ApiResponseValidationError(params.endpoint, summarizeIssues(result.error));
}

/**
 * Turn a zod error into something a human can act on without the payload in front of them: the
 * path of each offending field, and what was expected there. Values are never included — this
 * runs in customer browsers and the summary ends up in logs.
 */
function summarizeIssues(error: z.ZodError): string {
  const reported = error.issues.slice(0, MAX_REPORTED_ISSUES).map(issue => {
    const path = issue.path.length > 0 ? issue.path.join('.') : '(root)';
    return `${path}: ${issue.message}`;
  });
  const omittedCount = error.issues.length - reported.length;
  const omitted = omittedCount > 0 ? ` (and ${omittedCount} more)` : '';
  return `${reported.join('; ')}${omitted}`;
}
